import { z } from 'zod';

/** Every error response body from the Capsico API has this shape. */
export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    /** W25: short support code ("CAP-7K2Q9M") derived from the request id; also in every log line. */
    ref: z.string().optional(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

/** Every success response body wraps its payload in `data`. */
export const apiSuccess = <T extends z.ZodTypeAny>(data: T) => z.object({ data });
export type ApiSuccess<T> = { data: T };

/** Stable machine-readable error codes shared by backend and clients. */
export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RULE_VIOLATION: 'RULE_VIOLATION',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  RATE_LIMITED: 'RATE_LIMITED',
  IDEMPOTENCY_KEY_REQUIRED: 'IDEMPOTENCY_KEY_REQUIRED',
  IDEMPOTENCY_KEY_INVALID: 'IDEMPOTENCY_KEY_INVALID',
  IDEMPOTENCY_IN_PROGRESS: 'IDEMPOTENCY_IN_PROGRESS',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  /** W24: the app integrity check failed (enforce mode). Apps show "install from Play Store". */
  INTEGRITY_FAILED: 'integrity_failed',
  /** W24: a sensitive call came without a valid integrity pass; apps re-check and retry. */
  INTEGRITY_REQUIRED: 'integrity_required',
  /** W25: a protected public web form came without a Cloudflare Turnstile token. */
  TURNSTILE_REQUIRED: 'turnstile_required',
  /** W25: the Turnstile token was rejected (or replayed); the page shows a fresh check. */
  TURNSTILE_FAILED: 'turnstile_failed',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** Header names used across the platform. */
export const HEADERS = {
  IDEMPOTENCY_KEY: 'x-idempotency-key',
  IDEMPOTENT_REPLAYED: 'idempotent-replayed',
  REQUEST_ID: 'x-request-id',
  CACHE_STATUS: 'x-cache',
  /** W25: the CAP- support code of this request (same value as `error.ref`). */
  SUPPORT_REF: 'x-capsico-ref',
} as const;

/** W25: "CAP-" + 6 Crockford base32 characters (no I, L, O, U). */
export const SUPPORT_REF_PATTERN = /^CAP-[0-9A-HJKMNP-TV-Z]{6}$/;
